import { Phone, PhoneOff } from "lucide-react";
import { formatPhone } from "../../lib/format";
import { useActiveCalls } from "../../voice/useActiveCalls";
import { useVoice } from "../../voice/store";
import { voice } from "../../voice/manager";
import { Button } from "../../components/ui/Button";

/** Global incoming-call prompt, visible on every page. */
export function IncomingCallOverlay() {
  const incoming = useVoice((s) => s.incoming);
  const busy = useVoice((s) => !!s.activeCallSid);
  const { data: active = [] } = useActiveCalls();
  if (incoming.length === 0) return null;
  return (
    <div className="fixed inset-x-0 top-20 z-50 mx-auto flex w-[calc(100vw-2rem)] max-w-md flex-col gap-3" role="region" aria-label="Incoming calls">
      {incoming.map((call) => {
        const server = active.find((c) => c.myLeg?.callSid === call.callSid);
        const internal = !!server?.internal;
        const who = internal ? (server?.peer?.name ?? "A colleague") : server?.contact?.name ?? formatPhone(server?.externalNumber ?? call.from);
        const isTransfer = server?.role === "transfer_target";
        const isAdded = server?.role === "added";
        return (
          <div key={call.callSid} role="alertdialog" aria-labelledby={`in-${call.callSid}`} className="glass animate-float-in rounded-3xl p-5 shadow-2xl">
            <div className="mb-3 flex justify-center"><span className="bg-success-gradient grid h-14 w-14 animate-ring-pulse place-items-center rounded-full text-white"><Phone className="h-6 w-6" aria-hidden="true" /></span></div>
            <p className="text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-600">{isTransfer ? "Transferred call" : isAdded ? "Join a call" : internal ? "Call from a colleague" : "Incoming call"}</p>
            <p id={`in-${call.callSid}`} className="mt-1 text-center text-2xl font-semibold tracking-tight text-default">{who}</p>
            {!internal && server?.contact && <p className="text-center text-sm text-muted">{formatPhone(server.externalNumber)}</p>}
            {isAdded && server?.addedBy && <p className="text-center text-xs text-muted">{server.addedBy.name} is adding you to this call</p>}
            {server && !internal && !isAdded && <p className="text-center text-xs text-subtle">to {formatPhone(server.orgNumber)}</p>}
            {busy && <p className="mt-2 text-center text-xs text-amber-700">Finish your current call before answering.</p>}
            <div className="mt-4 flex gap-2">
              <Button variant="success" className="flex-1" onClick={() => voice.accept(call.callSid)} disabled={busy}>
                <Phone className="h-4 w-4" aria-hidden="true" /> Answer
              </Button>
              <Button variant="danger" className="flex-1" onClick={() => voice.reject(call.callSid)}>
                <PhoneOff className="h-4 w-4" aria-hidden="true" /> Decline
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
